import { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';

import Global from '../../../../../store/global-selectors';
import { areProductPropsEqual } from '../utils';
import styles from './styles.module.less';

/**
 * @param {import('../../../types').ColumnProps} props
 * @returns {React.ReactNode}
 */
const Column = ({ value }) => {
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const { product, updateProduct } = value;

  const onBlur = useCallback(
    (newValue) => {
      updateProduct(product.rowKey ?? String(product.productId), 'cost', newValue);
    },
    [product.rowKey, product.productId, updateProduct],
  );

  const summary = useMemo(() => {
    if (!product.isBundle) {
      return product.cost;
    }
    const bundleComponents = product.components || [];
    return bundleComponents
      .reduce((acc, component) => {
        return acc + component.quantity * (component.cost || 0);
      }, 0)
      .toFixed(2);
  }, [product.isBundle, product.components, product.cost]);

  return (
    <CurrencyInput
      className={classNames(styles['cost-input'], {
        [styles['cost-input-disabled']]: product.isBundle,
      })}
      currencyCode={currencyCode}
      currencySymbolClassName={classNames({
        [styles['currency-symbol-disabled']]: product.isBundle,
      })}
      disabled={product.isBundle}
      onBlur={onBlur}
      value={summary}
    />
  );
};

/**
 * @typedef {import('../../../types').ColumnProps} Props
 */

/**
 * @param {Props} prevProps
 * @param {Props} nextProps
 * @returns {boolean}
 */
const arePropsEqual = (prevProps, nextProps) => {
  return (
    areProductPropsEqual('cost')(prevProps, nextProps) &&
    prevProps.value.warehouseId === nextProps.value.warehouseId &&
    prevProps.value.product.quantity === nextProps.value.product.quantity &&
    prevProps.value.product.components === nextProps.value.product.components
  );
};

export const CostColumn = memo(Column, arePropsEqual);
